import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRawStockHistoryAction } from '../../redux/actions/rawStockHistory/rawStockHistoryAction';
import DataTable from 'react-data-table-component';
import { 
  Box, 
  TextField, 
  Typography, 
  CircularProgress, 
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportToCSV, reportHeaders } from '../../utils/exportUtils';

const RawStockHistoryTable = () => {
  const dispatch = useDispatch();
  const { 
    rawStockHistory, 
    loading, 
    error 
  } = useSelector((state) => state.rawStockHistory);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    dispatch(fetchRawStockHistoryAction());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchRawStockHistoryAction());
  };

  const handleExport = () => {
    const filename = `raw_stock_history_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(filteredHistory, filename, reportHeaders.rawStockHistory);
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'ADD':
        return 'success';
      case 'UPDATE':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredHistory = rawStockHistory.filter((item) => {
    const searchValue = filterText.toLowerCase();
    return (
      (item.stock_code && item.stock_code.toLowerCase().includes(searchValue)) ||
      (item.stock_name && item.stock_name.toLowerCase().includes(searchValue)) ||
      (item.stock_type_name && item.stock_type_name.toLowerCase().includes(searchValue)) ||
      (item.stone_type_name && item.stone_type_name.toLowerCase().includes(searchValue)) ||
      (item.karatage_name && item.karatage_name.toLowerCase().includes(searchValue)) ||
      (item.color_name && item.color_name.toLowerCase().includes(searchValue)) ||
      (item.reference_number && item.reference_number.toLowerCase().includes(searchValue)) ||
      (item.remarks && item.remarks.toLowerCase().includes(searchValue)) ||
      (item.created_by && item.created_by.toLowerCase().includes(searchValue)) ||
      (item.action_type && item.action_type.toLowerCase().includes(searchValue)) ||
      (item.batch_number && item.batch_number.toLowerCase().includes(searchValue)) ||
      (item.transaction_reference && item.transaction_reference.toLowerCase().includes(searchValue)) ||
      String(item.qty).toLowerCase().includes(searchValue) ||
      String(item.weight).toLowerCase().includes(searchValue) ||
      (item.created_at && new Date(item.created_at).toLocaleString().toLowerCase().includes(searchValue))
    );
  });

  const columns = [
    {
      name: <Typography fontWeight="bold" fontSize="14px">Date & Time</Typography>,
      selector: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A',
      sortable: true,
      width: '150px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Action</Typography>,
      cell: (row) => (
        <Chip 
          label={row.action_type} 
          color={getActionTypeColor(row.action_type)}
          size="small"
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Stock Code</Typography>,
      selector: (row) => row.stock_code || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Stock Name</Typography>,
      selector: (row) => row.stock_name || 'N/A',
      sortable: true,
      width: '150px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Stock Type</Typography>,
      selector: (row) => row.stock_type_name || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Quantity</Typography>,
      selector: (row) => row.qty || 0,
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Weight</Typography>,
      selector: (row) => row.weight || 0,
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Stone Type</Typography>,
      selector: (row) => row.stone_type_name || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Karatage</Typography>,
      selector: (row) => row.karatage_name || 'N/A',
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Color</Typography>,
      selector: (row) => row.color_name || 'N/A',
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Size</Typography>,
      selector: (row) => row.size || 'N/A',
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Reference</Typography>,
      selector: (row) => row.reference_number || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Remarks</Typography>,
      selector: (row) => row.remarks || 'N/A',
      sortable: true,
      width: '150px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Created By</Typography>,
      selector: (row) => row.created_by || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Batch Number</Typography>,
      selector: (row) => row.batch_number || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Transaction Ref</Typography>,
      selector: (row) => row.transaction_reference || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Previous Qty</Typography>,
      selector: (row) => row.previous_qty || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">Previous Weight</Typography>,
      selector: (row) => row.previous_weight || 'N/A',
      sortable: true,
      width: '130px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">New Qty</Typography>,
      selector: (row) => row.new_qty || 'N/A',
      sortable: true,
      width: '100px',
    },
    {
      name: <Typography fontWeight="bold" fontSize="14px">New Weight</Typography>,
      selector: (row) => row.new_weight || 'N/A',
      sortable: true,
      width: '110px',
    },
  ];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      {/* Header Row with Title & Search */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Raw Stock Addition History
          </Typography>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={loading || filteredHistory.length === 0}
            sx={{
              borderColor: '#2196f3',
              color: '#2196f3',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: 'rgba(33, 150, 243, 0.04)',
              },
            }}
          >
            Export CSV
          </Button>
          <FilterListIcon sx={{ color: 'text.secondary' }} />
          <TextField
            size="small"
            placeholder="Search history..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{
              width: '250px',
              '& .MuiInputBase-root': {
                backgroundColor: 'transparent',
              },
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error}
        </Alert>
      )}

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip 
          label={`Total Records: ${filteredHistory.length}`} 
          color="primary" 
          variant="outlined" 
          onClick={(e) => e.stopPropagation()}
        />
        <Chip 
          label={`Additions: ${filteredHistory.filter(h => h.action_type === 'ADD').length}`} 
          color="success" 
          variant="outlined" 
          onClick={(e) => e.stopPropagation()}
        />
        <Chip 
          label={`Updates: ${filteredHistory.filter(h => h.action_type === 'UPDATE').length}`} 
          color="warning" 
          variant="outlined" 
          onClick={(e) => e.stopPropagation()}
        />
        <Chip 
          label={`Deletions: ${filteredHistory.filter(h => h.action_type === 'DELETE').length}`} 
          color="error" 
          variant="outlined" 
          onClick={(e) => e.stopPropagation()}
        />
      </Box>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredHistory}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        highlightOnHover
        dense
        responsive
        noHeader
        progressPending={loading}
        progressComponent={<CircularProgress />}
        sortIcon={<FilterListIcon />}
        customStyles={{
          headRow: {
            style: {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
            },
          },
          rows: {
            style: {
              fontSize: '12px',
            },
          },
        }}
      />
    </Box>
  );
};

export default RawStockHistoryTable; 